import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Table, Button, Card, Spinner, Form, InputGroup, Pagination, Badge, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import axios from 'axios';
import { useUserContext } from '../layouts/UserContext';
import { getHatateToken } from "../helpers/utils";
import { UserService } from '../service/userService';
import debounce from 'lodash/debounce';
import { faEye, faTrashAlt, faUserGraduate, faSmile, faPaperclip, faTimes, faMinus, faExpand, faGripVertical, faList, faEdit, faCheck, faEllipsisV, faCog } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

type LanguageType = 'es' | 'en';

interface Conversation {
  id: string;
  name: string;
  lastMessageTime: string;
  firstMessageTime: string;
  messageCount: number;
  preview: string;
  phoneNumber?: string;
  isActive?: boolean;
}

interface Message {
  timestamp: string;
  sender: string;
  text: string;
  id?: string;
}

interface ConversationsTableProps {
  language: LanguageType;
  refreshInterval?: number;
}

interface AttachedFile {
  file: File;
  preview?: string;
  type: 'image' | 'document' | 'other';
}

interface ConversationWindow {
  id: string;
  conversationId: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  isMinimized: boolean;
  zIndex: number;
  messages: Message[];
  displayedMessages: Message[];
  loadingMessages: boolean;
  replyMessage: string;
  sendingReply: boolean;
  page: number;
  hasMoreMessages: boolean;
  attachedFiles: AttachedFile[];
  showEmojiPicker: boolean;
  autoScrollEnabled: boolean;
  messagesReady: boolean;
  isInitialLoad: boolean;
  // NUEVOS campos para control de actualización
  isAutoUpdateEnabled: boolean;
  lastUpdateTime: number;
  nextUpdateTime: number;
  updateIntervalRef?: NodeJS.Timeout;
  // NUEVO: Estado de edición de nombre
  isEditingName: boolean;
  editingName: string;
  savingName: boolean;
}

// NUEVA INTERFACE para la ventana de lista de conversaciones
interface ConversationsListWindow {
  id: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  isMinimized: boolean;
  zIndex: number;
  isVisible: boolean;
}

const API_BASE_URL = 'https://backend.anubisai.net/api/whatsapp/bot';
const API_KEY = 'anubis-whatsapp-api-key';
const API_SEND_URL = 'https://backend.anubisai.net/api/whatsapp/send';
const API_IMG_URL = 'https://backend.anubisai.net/api/bot';
// NUEVO: Endpoint para gestión de contactos
const API_CONTACT_URL = 'https://backend.anubisai.net/api/bot/agenda/contact';

// Número de mensajes a cargar en cada página
const PAGE_SIZE = 10;

// NUEVAS CONSTANTES para control de actualización
const MIN_UPDATE_INTERVAL = 10000; // 10 segundos
const MAX_UPDATE_INTERVAL = 60000; // 1 minuto
const WINDOW_POSITION_OFFSET = 30; // Offset para posiciones de ventanas

// Función para generar intervalo aleatorio
const getRandomUpdateInterval = (): number => {
  return Math.floor(Math.random() * (MAX_UPDATE_INTERVAL - MIN_UPDATE_INTERVAL + 1)) + MIN_UPDATE_INTERVAL;
};

// Función para generar posición aleatoria ordenada
const generateWindowPosition = (windowCount: number, isMobile: boolean = false): { x: number; y: number } => {
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const screenHeight = typeof window !== 'undefined' ? window.innerHeight : 768;
    
    // Calcular posición inicial centrada
    const baseX = Math.max(50, (screenWidth - 320) / 2);
    const baseY = Math.max(50, (screenHeight - 400) / 2);
    
    // Escalonar las ventanas con offset
    const offsetX = (windowCount * 30) % 200; // Reiniciar cada 6-7 ventanas
    const offsetY = (windowCount * 30) % 150; // Reiniciar cada 5 ventanas
    
    return {
      x: baseX + offsetX,
      y: baseY + offsetY
    };
  };

// Emojis más comunes organizados por categorías
const EMOJI_CATEGORIES = {
  smileys: ['😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩', '🥳'],
  gestures: ['👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👋', '🤚', '🖐️', '✋', '🖖', '👏', '🙌', '🤲', '🤝', '🙏'],
  hearts: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝'],
  objects: ['💼', '📱', '💻', '⌚', '📷', '📹', '🎵', '🎶', '🔔', '🔕', '📢', '📣', '📯', '🔈', '🔉', '🔊', '📻', '🎤', '🎧', '📺'],
  nature: ['🌞', '🌝', '🌛', '🌜', '🌚', '🌕', '🌖', '🌗', '🌘', '🌑', '🌒', '🌓', '🌔', '🌙', '⭐', '🌟', '💫', '✨', '☄️', '☀️']
};

const ConversationsTable: React.FC<ConversationsTableProps> = ({ 
  language, 
  refreshInterval = 5000
}) => {
  const [contactInfoMap, setContactInfoMap] = useState<{[key: string]: string}>({});
  // NUEVO: Mock de nombres de contactos
  const [contactNamesMap, setContactNamesMap] = useState<{[key: string]: string}>({});
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  
  // NUEVO: Estado para la ventana de lista de conversaciones
  const [conversationsListWindow, setConversationsListWindow] = useState<ConversationsListWindow>({
    id: 'conversations-list-window',
    position: { x: 50, y: 50 },
    size: { width: 800, height: 600 },
    isMinimized: false,
    zIndex: 999,
    isVisible: false
  });
  
  // Estados para ventanas de conversación - MODIFICADO para incluir auto-update
  const [conversationWindows, setConversationWindows] = useState<ConversationWindow[]>([]);
  const [nextZIndex, setNextZIndex] = useState<number>(1000);
  const [draggedWindow, setDraggedWindow] = useState<string | null>(null);
  const [resizedWindow, setResizedWindow] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  
  // Estados para control de drag
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStartTime, setDragStartTime] = useState<number>(0);
  const [dragStartPosition, setDragStartPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  
  // Estados para entrenamiento ML
  const [trainingModalVisible, setTrainingModalVisible] = useState<boolean>(false);
  const [trainingMLConversationId, setTrainingMLConversationId] = useState<string | null>(null);
  const [trainingMLInProgress, setTrainingMLInProgress] = useState<boolean>(false);
  const [trainingMLResult, setTrainingMLResult] = useState<{success: boolean, message: string, examplesAdded?: number} | null>(null);
  const [conversationNameToTrain, setConversationNameToTrain] = useState<string>('');
  
  // Estados para eliminación
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [conversationToDelete, setConversationToDelete] = useState<string | null>(null);
  const [deletingConversation, setDeletingConversation] = useState<boolean>(false);
  const [conversationNameToDelete, setConversationNameToDelete] = useState<string>('');
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRefs = useRef<{[key: string]: HTMLInputElement | null}>({});
  const messagesEndRefs = useRef<{[key: string]: HTMLDivElement | null}>({});
  
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  const isConversationActive = (lastActivityTime: string): boolean => {
    const currentTime = new Date().getTime();
    const lastMessageTime = new Date(lastActivityTime).getTime();
    const fiveMinutesInMs = 5 * 60 * 1000;
    
    return (currentTime - lastMessageTime) <= fiveMinutesInMs;
  };

  // NUEVA FUNCIÓN: Cargar nombres desde el endpoint al inicio
  const loadContactNamesFromServer = useCallback(async (): Promise<void> => {
    try {
      const API_TOKEN_KEY = getHatateToken();
      
      const response = await axios.get(`${API_CONTACT_URL}/all`, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY,
          'Authorization': `${API_TOKEN_KEY}`
        },
        timeout: 5000
      });

      if (response.data.success && response.data.contacts) {
        const serverNames: {[key: string]: string} = {};
        
        response.data.contacts.forEach((contact: {whatsappId: string, name: string}) => {
          if (contact.whatsappId && contact.name) {
            serverNames[contact.whatsappId] = contact.name;
          }
        });

        setContactNamesMap(prev => ({
          ...serverNames,
          ...prev
        }));
        
        console.log('✅ Nombres cargados desde servidor:', Object.keys(serverNames).length);
      }
    } catch (error) {
      console.warn('⚠️ No se pudieron cargar nombres del servidor:', error);
    }
  }, []);

  // NUEVA FUNCIÓN: Cargar nombres en batch para conversaciones
  const loadContactNamesForConversations = useCallback(async (conversations: Conversation[]): Promise<void> => {
    const conversationIds = conversations.map(conv => conv.id);
    const missingNames = conversationIds.filter(id => !contactNamesMap[id]);
    
    if (missingNames.length === 0) return;

    try {
      const API_TOKEN_KEY = getHatateToken();
      
      const response = await axios.post(`${API_CONTACT_URL}/batch`, 
        {
          whatsappIds: missingNames
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': API_KEY,
            'Authorization': `${API_TOKEN_KEY}`
          },
          timeout: 5000
        }
      );

      if (response.data.success && response.data.contacts) {
        const newNames: {[key: string]: string} = {};
        
        response.data.contacts.forEach((contact: {whatsappId: string, name: string}) => {
          if (contact.whatsappId && contact.name) {
            newNames[contact.whatsappId] = contact.name;
          }
        });

        if (Object.keys(newNames).length > 0) {
          setContactNamesMap(prev => ({
            ...prev,
            ...newNames
          }));
          console.log('✅ Nombres cargados para conversaciones:', Object.keys(newNames).length);
        }
      }
    } catch (error) {
      console.warn('⚠️ Error cargando nombres en batch:', error);
    }
  }, [contactNamesMap]);

  // NUEVA FUNCIÓN: Obtener información del remitente del mensaje
  const getMessageSenderInfo = useCallback((message: Message, conversationId: string): {displayName: string, phoneNumber: string} => {
    if (message.sender === 'bot') {
      return {
        displayName: 'Bot',
        phoneNumber: ''
      };
    }

    // Extraer número de teléfono del conversationId o del sender
    let phoneNumber = '';
    if (conversationId.includes('@c.us')) {
      phoneNumber = conversationId.split('@c.us')[0];
    } else if (message.sender && message.sender !== 'bot' && message.sender !== 'user') {
      phoneNumber = message.sender;
    }

    // Buscar nombre personalizado
    const customName = contactNamesMap[conversationId] || contactNamesMap[phoneNumber];
    
    if (customName) {
      return {
        displayName: customName,
        phoneNumber: formatPhoneNumber(phoneNumber)
      };
    }

    // Si no hay nombre personalizado, mostrar teléfono formateado
    if (phoneNumber) {
      const formattedPhone = formatPhoneNumber(phoneNumber);
      return {
        displayName: formattedPhone,
        phoneNumber: formattedPhone
      };
    }

    // Fallback
    return {
      displayName: message.sender || 'Usuario',
      phoneNumber: ''
    };
  }, [contactNamesMap]);

  // NUEVA FUNCIÓN: Guardar nombre de contacto con fallback mock
  const saveContactName = useCallback(async (contactId: string, contactName: string): Promise<boolean> => {
    try {
      // Primero guardar en mock local (respuesta inmediata)
      setContactNamesMap(prev => ({
        ...prev,
        [contactId]: contactName
      }));

      // Intentar enviar al endpoint
      const API_TOKEN_KEY = getHatateToken();
      
      const response = await axios.post(API_CONTACT_URL, 
        {
          whatsappId: contactId,
          name: contactName
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': API_KEY,
            'Authorization': `${API_TOKEN_KEY}`
          },
          timeout: 5000 // 5 segundos de timeout
        }
      );

      if (response.data.success) {
        console.log('✅ Nombre guardado en servidor:', contactId, '→', contactName);
        return true;
      } else {
        console.warn('⚠️ Error del servidor al guardar nombre, manteniendo mock local');
        return false;
      }
    } catch (error) {
      console.warn('⚠️ Endpoint no disponible, nombre guardado localmente:', error);
      return false;
    }
  }, []);

  // NUEVA FUNCIÓN: Obtener nombre de contacto (mock + server)
  const getContactDisplayName = useCallback((conversation: Conversation): string => {
    // Prioridad: 1. Mock local, 2. Nombre de conversación, 3. Formato número
    if (contactNamesMap[conversation.id]) {
      return contactNamesMap[conversation.id];
    }
    
    return formatContactName(conversation);
  }, [contactNamesMap]);

  // Función para formatear y mostrar nombres de contacto o grupo
  const formatContactName = (conversation: Conversation): string => {
    if (conversation.name && 
        conversation.name !== conversation.id && 
        !conversation.name.includes('@')) {
      return conversation.name;
    }
    
    if (conversation.id.includes('@g.us')) {
      const groupParts = conversation.id.split('@g.us')[0];
      const groupNamePart = groupParts.split('-').pop() || groupParts;
      
      return language === 'es' 
        ? `Grupo ${groupNamePart}` 
        : `Group ${groupNamePart}`;
    } else if (conversation.id.includes('@c.us')) {
      const phoneNumber = conversation.id.split('@c.us')[0];
      return formatPhoneNumber(phoneNumber);
    }
    
    return conversation.id;
  };

  // Función para formatear números de teléfono
  const formatPhoneNumber = (phoneNumber: string): string => {
    const numericPhone = phoneNumber.replace(/\D/g, '');
    
    if (numericPhone.length > 10) {
      const countryCode = numericPhone.substring(0, numericPhone.length - 10);
      const localNumber = numericPhone.substring(numericPhone.length - 10);
      
      let formattedNumber = `+${countryCode} `;
      
      if (localNumber.length === 10) {
        formattedNumber += `${localNumber.substring(0, 3)}-${localNumber.substring(3, 6)}-${localNumber.substring(6)}`;
      } else {
        formattedNumber += localNumber;
      }
      
      return formattedNumber;
    }
    
    if (numericPhone.length === 10) {
      return `${numericPhone.substring(0, 3)}-${numericPhone.substring(3, 6)}-${numericPhone.substring(6)}`;
    }
    
    return phoneNumber;
  };

  // NUEVAS FUNCIONES para control de auto-actualización de ventanas
  const setupWindowAutoUpdate = useCallback((windowId: string) => {
    const window = conversationWindows.find(w => w.id === windowId);
    if (!window || !window.isAutoUpdateEnabled) return;

    // Limpiar intervalo anterior si existe
    if (window.updateIntervalRef) {
      clearTimeout(window.updateIntervalRef);
    }

    const currentTime = Date.now();
    const timeToNext = Math.max(0, window.nextUpdateTime - currentTime);

    const timeoutId = setTimeout(() => {
      // Verificar si la ventana sigue siendo válida y tiene auto-update habilitado
      const currentWindow = conversationWindows.find(w => w.id === windowId);
      if (currentWindow && currentWindow.isAutoUpdateEnabled && !currentWindow.isMinimized) {
        fetchConversationDetail(currentWindow.conversationId, windowId, true);
        
        // Programar próxima actualización
        const nextInterval = getRandomUpdateInterval();
        const nextTime = Date.now() + nextInterval;
        
        setConversationWindows(windows => 
          windows.map(w => 
            w.id === windowId 
              ? { 
                  ...w, 
                  lastUpdateTime: Date.now(),
                  nextUpdateTime: nextTime
                }
              : w
          )
        );
        
        // Recursivamente programar la siguiente actualización
        setTimeout(() => setupWindowAutoUpdate(windowId), 100);
      }
    }, timeToNext);

    // Guardar la referencia del timeout en el estado
    setConversationWindows(windows => 
      windows.map(w => 
        w.id === windowId 
          ? { ...w, updateIntervalRef: timeoutId }
          : w
      )
    );
  }, [conversationWindows]);

  const toggleWindowAutoUpdate = (windowId: string) => {
    setConversationWindows(windows => 
      windows.map(w => {
        if (w.id === windowId) {
          const newAutoUpdateState = !w.isAutoUpdateEnabled;
          
          // Si se está deshabilitando, limpiar el timeout
          if (!newAutoUpdateState && w.updateIntervalRef) {
            clearTimeout(w.updateIntervalRef);
          }
          
          // Si se está habilitando, configurar próxima actualización
          if (newAutoUpdateState) {
            const nextInterval = getRandomUpdateInterval();
            setTimeout(() => setupWindowAutoUpdate(windowId), 100);
            return {
              ...w,
              isAutoUpdateEnabled: newAutoUpdateState,
              nextUpdateTime: Date.now() + nextInterval,
              lastUpdateTime: Date.now()
            };
          }
          
          return {
            ...w,
            isAutoUpdateEnabled: newAutoUpdateState,
            updateIntervalRef: undefined
          };
        }
        return w;
      })
    );
  };

  // NUEVAS FUNCIONES para edición de nombres en ventanas
  const startEditingName = (windowId: string) => {
    const window = conversationWindows.find(w => w.id === windowId);
    if (!window) return;
    
    const conversation = conversations.find(c => c.id === window.conversationId);
    const currentName = getContactDisplayName(conversation!);
    
    setConversationWindows(windows => 
      windows.map(w => 
        w.id === windowId 
          ? { 
              ...w, 
              isEditingName: true, 
              editingName: currentName 
            }
          : w
      )
    );
  };

  const cancelEditingName = (windowId: string) => {
    setConversationWindows(windows => 
      windows.map(w => 
        w.id === windowId 
          ? { 
              ...w, 
              isEditingName: false, 
              editingName: '' 
            }
          : w
      )
    );
  };

  const saveEditingName = async (windowId: string) => {
    const window = conversationWindows.find(w => w.id === windowId);
    if (!window || !window.editingName.trim()) {
      cancelEditingName(windowId);
      return;
    }

    setConversationWindows(windows => 
      windows.map(w => 
        w.id === windowId 
          ? { ...w, savingName: true }
          : w
      )
    );

    const success = await saveContactName(window.conversationId, window.editingName.trim());
    
    setConversationWindows(windows => 
      windows.map(w => 
        w.id === windowId 
          ? { 
              ...w, 
              isEditingName: false, 
              editingName: '', 
              savingName: false 
            }
          : w
      )
    );

    if (success) {
      console.log('✅ Nombre guardado exitosamente');
    } else {
      console.log('⚠️ Nombre guardado localmente, se sincronizará cuando el servidor esté disponible');
    }
  };

  const handleEditingNameKeyPress = (windowId: string, event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      saveEditingName(windowId);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancelEditingName(windowId);
    }
  };

  // NUEVAS FUNCIONES para manejar la ventana de lista de conversaciones
const openConversationsListWindow = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const screenHeight = typeof window !== 'undefined' ? window.innerHeight : 768;
    
    // Calcular posición centrada
    const width = isMobile ? Math.min(400, screenWidth - 40) : 600;
    const height = isMobile ? Math.min(500, screenHeight - 100) : 500;
    const x = (screenWidth - width) / 2;
    const y = (screenHeight - height) / 2;
    
    setConversationsListWindow(prev => ({
      ...prev,
      isVisible: true,
      isMinimized: false,
      zIndex: nextZIndex,
      position: { x, y },
      size: { width, height }
    }));
    setNextZIndex(prev => prev + 1);
  };

  const closeConversationsListWindow = () => {
    setConversationsListWindow(prev => ({
      ...prev,
      isVisible: false
    }));
  };

  const minimizeConversationsListWindow = () => {
    setConversationsListWindow(prev => ({
      ...prev,
      isMinimized: true
    }));
  };

  const restoreConversationsListWindow = () => {
    setConversationsListWindow(prev => ({
      ...prev,
      isMinimized: false,
      zIndex: nextZIndex
    }));
    setNextZIndex(prev => prev + 1);
  };

  const bringConversationsListToFront = () => {
    setConversationsListWindow(prev => ({
      ...prev,
      zIndex: nextZIndex
    }));
    setNextZIndex(prev => prev + 1);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    
    const newTotalPages = Math.ceil(filteredConversations.length / newItemsPerPage);
    setCurrentPage(prevPage => Math.min(prevPage, newTotalPages));
  };

  // Filtrar conversaciones según el término de búsqueda
  const filteredConversations = useMemo(() => {
    if (!searchTerm) return conversations;
    
    return conversations.filter(conversation => {
      const displayName = getContactDisplayName(conversation);
      return displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
             conversation.preview.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [conversations, searchTerm, getContactDisplayName]);

  // Paginación de conversaciones
  const paginatedConversations = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredConversations.slice(startIndex, endIndex);
  }, [filteredConversations, currentPage, itemsPerPage]);

  // Calcular número total de páginas
  const totalPages = Math.ceil(filteredConversations.length / itemsPerPage);

  const {
    user,
    isLoading: isUserLoading
  } = useUserContext();

  if (isUserLoading) {
    return (
      <>
        <div className="text-center my-5">
          <Spinner animation="border" />
          <p className="mt-2">{language === 'es' ? 'Cargando...' : 'Loading...'}</p>
        </div>
      </>
    );
  }
  
  const whatsAppServerPort = user.mlStuff.whatsAppServerPort;
  const whatsAppApiPort = user.mlStuff.whatsAppApiPort;
  const mlServerPort = user.mlStuff.mlServerPort;

  // Contact info extraction function
  const extractContactName = (response: string) => {
    const namePatterns = [
      /nombre\s*[:|=]?\s*(\w+(\s+\w+)*)/i,
      /alias\s*[:|=]?\s*(\w+(\s+\w+)*)/i,
      /apodo\s*[:|=]?\s*(\w+(\s+\w+)*)/i,
      /^(\w+(\s+\w+)*)$/
    ];

    for (const pattern of namePatterns) {
      const match = response.match(pattern);
      if (match && match[1]) {
        return match[1].trim();
      }
    }

    return null;
  };

  // Fetch contact info for specific sender
  const fetchContactInfo = useCallback(async (sender: string) => {
    try {
      if (sender === 'bot') return null;
      if (contactInfoMap[sender]) return contactInfoMap[sender];

      const timestamp = new Date().toISOString();
      const API_TOKEN_KEY = getHatateToken();

      const response = await axios.post(`${API_IMG_URL}/llm`, 
        { 
          prompt: [{ 
            content: 'Prueba llm',
            role: 'user',
            time: timestamp
          }],
          chatId: `whatsapp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
          skill: 'ambipar_v1',
          module: 'ambipar_hazmat_tech'
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `${API_TOKEN_KEY}`
          },
          data: {
            mlStuff: {
              whatsAppServerPort: whatsAppServerPort,
              whatsAppApiPort: whatsAppApiPort,
              mlServerPort: mlServerPort
            }
          }
        }
      );

      if (response.data.success && response.data.response) {
        const extractedName = extractContactName(response.data.response);
        
        if (extractedName) {
          setContactInfoMap(prev => ({
            ...prev,
            [sender]: extractedName
          }));
          return extractedName;
        }
      }
      return null;
    } catch (error) {
      console.error('Error fetching contact info:', error);
      return null;
    }
  }, [
    contactInfoMap, 
    whatsAppServerPort, 
    whatsAppApiPort, 
    mlServerPort
  ]);

  // Función para scroll automático al último mensaje
  const scrollToBottom = useCallback((windowId: string) => {
    setTimeout(() => {
      const messagesEnd = messagesEndRefs.current[windowId];
      if (messagesEnd) {
        messagesEnd.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  }, []);

  // Funciones de drag and drop para la ventana de lista de conversaciones
  const startDragConversationsList = (event: React.MouseEvent | React.TouchEvent) => {
    event.preventDefault();
    
    const currentTime = Date.now();
    setDragStartTime(currentTime);
    
    // Manejar tanto mouse como touch events
    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;
    
    setDragStartPosition({ x: clientX, y: clientY });
    setIsDragging(false);

    setDragOffset({
      x: clientX - conversationsListWindow.position.x,
      y: clientY - conversationsListWindow.position.y
    });
    setDraggedWindow('conversations-list-window');
    bringConversationsListToFront();
  };

  const handleDragConversationsList = useCallback((event: MouseEvent | TouchEvent) => {
    if (draggedWindow !== 'conversations-list-window') return;

    // Manejar tanto mouse como touch events
    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;

    // Detectar si realmente se está arrastrando (movimiento mínimo de 5px)
    const dragDistance = Math.sqrt(
      Math.pow(clientX - dragStartPosition.x, 2) + 
      Math.pow(clientY - dragStartPosition.y, 2)
    );
    
    if (dragDistance > 5) {
      setIsDragging(true);
    }

    const newX = clientX - dragOffset.x;
    const newY = clientY - dragOffset.y;

    // Límites de pantalla con responsive - más permisivos en móvil
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const screenHeight = typeof window !== 'undefined' ? window.innerHeight : 768;
    
    const minX = isMobile ? -150 : 0; // Permitir salir parcialmente en móvil
    const maxX = isMobile ? screenWidth - 50 : screenWidth - 100; // Mantener parte visible
    const minY = isMobile ? 60 : 0; // Evitar headers del sistema
    const maxY = isMobile ? screenHeight - 50 : screenHeight - 100;

    setConversationsListWindow(prev => ({
      ...prev,
      position: { 
        x: Math.max(minX, Math.min(maxX, newX)), 
        y: Math.max(minY, Math.min(maxY, newY)) 
      }
    }));
  }, [draggedWindow, dragOffset, dragStartPosition, conversationsListWindow.position]);

  // Funciones de resize para la ventana de lista
  const startResizeConversationsList = (event: React.MouseEvent) => {
    event.stopPropagation();
    setResizedWindow('conversations-list-window');
    bringConversationsListToFront();
  };

  const handleResizeConversationsList = useCallback((event: MouseEvent) => {
    if (resizedWindow !== 'conversations-list-window') return;

    const rect = document.getElementById('conversations-list-window')?.getBoundingClientRect();
    if (!rect) return;

    const newWidth = Math.max(400, event.clientX - rect.left);
    const newHeight = Math.max(300, event.clientY - rect.top);

    setConversationsListWindow(prev => ({
      ...prev,
      size: { width: newWidth, height: newHeight }
    }));
  }, [resizedWindow]);

  // Funciones para manejar ventanas de conversación - MODIFICADO para nuevas posiciones
  const createConversationWindow = (conversationId: string): ConversationWindow => {
    const windowId = `window-${conversationId}-${Date.now()}`;
    const windowCount = conversationWindows.length;
    
    // Detectar si es móvil de forma segura
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    
    // NUEVAS posiciones usando la función de posicionamiento mejorada
    const position = generateWindowPosition(windowCount, isMobile);
    const width = isMobile ? Math.min(280, (typeof window !== 'undefined' ? window.innerWidth : 320) - 40) : 300;
    const height = isMobile ? Math.min(350, (typeof window !== 'undefined' ? window.innerHeight : 600) - 180) : 400;
    
    // Configurar auto-update por defecto como habilitado
    const firstUpdateInterval = getRandomUpdateInterval();
    
    return {
      id: windowId,
      conversationId,
      position,
      size: { width, height },
      isMinimized: false,
      zIndex: nextZIndex,
      messages: [],
      displayedMessages: [],
      loadingMessages: true,
      replyMessage: '',
      sendingReply: false,
      page: 1,
      hasMoreMessages: true,
      attachedFiles: [],
      showEmojiPicker: false,
      autoScrollEnabled: true,
      messagesReady: false,
      isInitialLoad: true,
      // NUEVOS campos para auto-update
      isAutoUpdateEnabled: true,
      lastUpdateTime: Date.now(),
      nextUpdateTime: Date.now() + firstUpdateInterval,
      // NUEVOS campos para edición de nombre
      isEditingName: false,
      editingName: '',
      savingName: false
    };
  };

  const openConversationWindow = (conversationId: string) => {
    // Verificar si ya existe una ventana para esta conversación
    const existingWindow = conversationWindows.find(w => w.conversationId === conversationId);
    
    if (existingWindow) {
      // Si está minimizada, restaurarla
      if (existingWindow.isMinimized) {
        setConversationWindows(windows => 
          windows.map(w => 
            w.id === existingWindow.id 
              ? { ...w, isMinimized: false, zIndex: nextZIndex }
              : w
          )
        );
        setNextZIndex(prev => prev + 1);
      } else {
        // Solo traerla al frente
        setConversationWindows(windows => 
          windows.map(w => 
            w.id === existingWindow.id 
              ? { ...w, zIndex: nextZIndex }
              : w
          )
        );
        setNextZIndex(prev => prev + 1);
      }
      return;
    }

    const newWindow = createConversationWindow(conversationId);
    setConversationWindows(prev => [...prev, newWindow]);
    setNextZIndex(prev => prev + 1);
    
    // Cargar mensajes y configurar auto-update
    fetchConversationDetail(conversationId, newWindow.id);
    
    // Configurar auto-update después de un breve delay
    setTimeout(() => setupWindowAutoUpdate(newWindow.id), 1000);
  };

  const closeConversationWindow = (windowId: string) => {
    // Limpiar timeout antes de cerrar
    const window = conversationWindows.find(w => w.id === windowId);
    if (window?.updateIntervalRef) {
      clearTimeout(window.updateIntervalRef);
    }
    
    setConversationWindows(windows => windows.filter(w => w.id !== windowId));
    // Limpiar referencias
    delete messagesEndRefs.current[windowId];
    delete fileInputRefs.current[windowId];
  };

  const minimizeConversationWindow = (windowId: string) => {
    setConversationWindows(windows => 
      windows.map(w => 
        w.id === windowId 
          ? { ...w, isMinimized: true }
          : w
      )
    );
  };

  const restoreConversationWindow = (windowId: string) => {
    setConversationWindows(windows => 
      windows.map(w => 
        w.id === windowId 
          ? { ...w, isMinimized: false, zIndex: nextZIndex }
          : w
      )
    );
    setNextZIndex(prev => prev + 1);
  };

  const bringToFront = (windowId: string) => {
    setConversationWindows(windows => 
      windows.map(w => 
        w.id === windowId 
          ? { ...w, zIndex: nextZIndex }
          : w
      )
    );
    setNextZIndex(prev => prev + 1);
  };

  // Funciones de drag and drop corregidas para móviles (ventanas de conversación)
  const startDrag = (windowId: string, event: React.MouseEvent | React.TouchEvent) => {
    event.preventDefault();
    const window = conversationWindows.find(w => w.id === windowId);
    if (!window) return;

    const currentTime = Date.now();
    setDragStartTime(currentTime);
    
    // Manejar tanto mouse como touch events
    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;
    
    setDragStartPosition({ x: clientX, y: clientY });
    setIsDragging(false);

    setDragOffset({
      x: clientX - window.position.x,
      y: clientY - window.position.y
    });
    setDraggedWindow(windowId);
    bringToFront(windowId);
  };

const handleDrag = useCallback((event: MouseEvent | TouchEvent) => {
    if (!draggedWindow) return;

    // Manejar tanto mouse como touch events
    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;

    // Detectar si realmente se está arrastrando (movimiento mínimo de 5px)
    const dragDistance = Math.sqrt(
      Math.pow(clientX - dragStartPosition.x, 2) + 
      Math.pow(clientY - dragStartPosition.y, 2)
    );
    
    if (dragDistance > 5) {
      setIsDragging(true);
    }

    const newX = clientX - dragOffset.x;
    const newY = clientY - dragOffset.y;

    // LIBERTAD TOTAL DE MOVIMIENTO - sin límites restrictivos
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const screenHeight = typeof window !== 'undefined' ? window.innerHeight : 768;
    
    // Límites muy permisivos - solo evitar que se pierdan completamente
    const minX = -200; // Permitir salir bastante fuera de pantalla
    const maxX = screenWidth + 100;
    const minY = -100;
    const maxY = screenHeight + 100;

    if (draggedWindow === 'conversations-list-window') {
      setConversationsListWindow(prev => ({
        ...prev,
        position: { 
          x: Math.max(minX, Math.min(maxX, newX)), 
          y: Math.max(minY, Math.min(maxY, newY)) 
        }
      }));
    } else {
      setConversationWindows(windows =>
        windows.map(w =>
          w.id === draggedWindow
            ? { 
                ...w, 
                position: { 
                  x: Math.max(minX, Math.min(maxX, newX)), 
                  y: Math.max(minY, Math.min(maxY, newY)) 
                } 
              }
            : w
        )
      );
    }
  }, [draggedWindow, dragOffset, dragStartPosition]);

  const endDrag = useCallback(() => {
    const wasDragging = isDragging;
    const dragDuration = Date.now() - dragStartTime;
    
    setDraggedWindow(null);
    setDragOffset({ x: 0, y: 0 });
    setIsDragging(false);
    setDragStartTime(0);
    setDragStartPosition({ x: 0, y: 0 });
    
    return { wasDragging, dragDuration };
  }, [isDragging, dragStartTime]);

  // Funciones de resize
  const startResize = (windowId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setResizedWindow(windowId);
    bringToFront(windowId);
  };

  const handleResize = useCallback((event: MouseEvent) => {
    if (resizedWindow === 'conversations-list-window') {
      handleResizeConversationsList(event);
      return;
    }

    if (!resizedWindow) return;

    const window = conversationWindows.find(w => w.id === resizedWindow);
    if (!window) return;

    const rect = document.getElementById(resizedWindow)?.getBoundingClientRect();
    if (!rect) return;

    const newWidth = Math.max(300, event.clientX - rect.left);
    const newHeight = Math.max(200, event.clientY - rect.top);

    setConversationWindows(windows =>
      windows.map(w =>
        w.id === resizedWindow
          ? { ...w, size: { width: newWidth, height: newHeight } }
          : w
      )
    );
  }, [resizedWindow, conversationWindows, handleResizeConversationsList]);

  const endResize = useCallback(() => {
    setResizedWindow(null);
  }, []);

  // Event listeners para drag y resize con soporte móvil
  useEffect(() => {
    if (draggedWindow) {
      const handleMouseMove = (e: MouseEvent) => handleDrag(e);
      const handleTouchMove = (e: TouchEvent) => {
        e.preventDefault(); // Prevenir scroll del navegador
        handleDrag(e);
      };
      const handleMouseUp = () => endDrag();
      const handleTouchEnd = () => endDrag();

      // Mouse events
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      // Touch events para móviles
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [draggedWindow, handleDrag, endDrag]);

  useEffect(() => {
    if (resizedWindow) {
      document.addEventListener('mousemove', handleResize);
      document.addEventListener('mouseup', endResize);
    }
    return () => {
      document.removeEventListener('mousemove', handleResize);
      document.removeEventListener('mouseup', endResize);
    };
  }, [resizedWindow, handleResize, endResize]);

  // NUEVO useEffect para limpiar timeouts al desmontar el componente
  useEffect(() => {
    return () => {
      // Limpiar todos los timeouts de las ventanas al desmontar
      conversationWindows.forEach(window => {
        if (window.updateIntervalRef) {
          clearTimeout(window.updateIntervalRef);
        }
      });
    };
  }, []);

  // NUEVO: useEffect para cargar nombres al inicio
  useEffect(() => {
    loadContactNamesFromServer();
  }, [loadContactNamesFromServer]);

  // NUEVO: useEffect para cargar nombres cuando cambien las conversaciones
  useEffect(() => {
    if (conversations.length > 0) {
      loadContactNamesForConversations(conversations);
    }
  }, [conversations, loadContactNamesForConversations]);

  // Función para obtener las conversaciones - MODIFICADA para cargar nombres
const fetchConversations = useCallback(async (forceRefresh: boolean = false, silentUpdate: boolean = true) => {
    try {
      // Solo mostrar loading si no es actualización silenciosa Y no hay conversaciones
      if (!silentUpdate && conversations.length === 0) {
        setLoading(true);
      }
      
      console.log('Consultando endpoint:', `${API_BASE_URL}/conversations`);
      const timestamp = new Date().toISOString();
      const API_TOKEN_KEY = getHatateToken();

      const response = await axios.get(`${API_BASE_URL}/conversations`, {
        headers: { 
          'x-api-key': API_KEY,
          'Authorization': `${API_TOKEN_KEY}`
        },
        data: {
          prompt: [{ 
            content: '',
            role: 'user',
            time: timestamp
          }],
          chatId: `whatsapp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
          skill: 'ambipar_v1',
          module: 'ambipar_hazmat_tech',
          mlStuff: {
            whatsAppServerPort: whatsAppServerPort,
            whatsAppApiPort: whatsAppApiPort,
            mlServerPort: mlServerPort
          }
        }
      });
      
      console.log('Respuesta recibida:', response.data);
  
      if (response.data.success) {
        const newConversations = (response.data.conversations || []).map((conv: Conversation) => ({
          ...conv,
          isActive: isConversationActive(conv.lastMessageTime)
        }));
        console.log('Actualizando conversaciones:', newConversations);
        
        setConversations(newConversations);
        setLastUpdate(new Date());
        setError(null);
        
        // NUEVO: Cargar nombres para las nuevas conversaciones
        loadContactNamesForConversations(newConversations);
      } else {
        // Solo mostrar error si no es actualización silenciosa
        if (!silentUpdate) {
          setError(response.data.message || 'Error al cargar conversaciones');
        }
      }
    } catch (error) {
      console.error('Error al obtener conversaciones:', error);
      // Solo mostrar error si no es actualización silenciosa
      if (!silentUpdate) {
        setError(language === 'es' 
          ? 'No se pudieron cargar las conversaciones. Intente más tarde.' 
          : 'Could not load conversations. Please try again later.');
      }
    } finally {
      // Solo ocultar loading si no era actualización silenciosa
      if (!silentUpdate) {
        setLoading(false);
      }
    }
  }, [conversations.length, whatsAppServerPort, whatsAppApiPort, mlServerPort, language, loadContactNamesForConversations]);

   // Función mejorada para abrir conversación al hacer clic en el item
const handleConversationClick = (conversationId: string) => {
    openConversationWindow(conversationId);
  };

const handleDeleteChatById = async (conversationId: string) => {
    try {
      const API_TOKEN_KEY = getHatateToken();

      const response = await axios.post(`${API_BASE_URL}/conversation-delete?id=${conversationId}`, 
        {},
        {
          headers: { 
            'x-api-key': API_KEY,
            'Authorization': `${API_TOKEN_KEY}`
          },
          data: {
            mlStuff: {
              whatsAppServerPort: whatsAppServerPort,
              whatsAppApiPort: whatsAppApiPort,
              mlServerPort: mlServerPort
            }
          }
        }
      );

      if (response.data.success) {
        // Actualizar lista de conversaciones
        fetchConversations(true, false);
        
        // Cerrar ventanas de esta conversación
        setConversationWindows(windows => 
          windows.filter(w => w.conversationId !== conversationId)
        );
        
        return true;
      } else {
        console.error('Error al eliminar chat:', response.data.message);
        return false;
      }
    } catch (error) {
      console.error('Error al eliminar chat:', error);
      return false;
    }
  };
  // Función para obtener detalles de una conversación para una ventana específica - SIN CAMBIOS
  const fetchConversationDetail = useCallback(async (conversationId: string, windowId: string, silentUpdate: boolean = false) => {
    try {
      const API_TOKEN_KEY = getHatateToken();

      const response = await axios.get(`${API_BASE_URL}/conversation-detail`, {
        headers: {
          'x-api-key': API_KEY,
          'Authorization': `${API_TOKEN_KEY}`
        },
        data: {
          mlStuff: {
            whatsAppServerPort: whatsAppServerPort,
            whatsAppApiPort: whatsAppApiPort,
            mlServerPort: mlServerPort
          }
        },
        params: { 
          id: conversationId 
        }
      });
      
      if (response.data.success) {
        const messagesWithIds = (response.data.messages || []).map((msg: Message, index: number) => ({
          ...msg,
          id: msg.id || `${conversationId}-${index}-${msg.timestamp}-${msg.sender}`
        }));
        
        setConversationWindows(windows => 
          windows.map(w => {
            if (w.id === windowId) {
              const startIdx = Math.max(0, messagesWithIds.length - (w.page * PAGE_SIZE));
              const endIdx = messagesWithIds.length;
              const displayedMessages = messagesWithIds.slice(startIdx, endIdx);
              
              return {
                ...w,
                messages: messagesWithIds,
                displayedMessages,
                loadingMessages: false,
                messagesReady: true,
                isInitialLoad: false,
                hasMoreMessages: startIdx > 0
              };
            }
            return w;
          })
        );
        
        // Scroll automático al último mensaje después de cargar
        if (!silentUpdate) {
          scrollToBottom(windowId);
        }
        
        return messagesWithIds;
      } else {
        console.error('Error al cargar mensajes:', response.data.message);
        setConversationWindows(windows => 
          windows.map(w => 
            w.id === windowId 
              ? { ...w, loadingMessages: false, messagesReady: true }
              : w
          )
        );
        return [];
      }
    } catch (error) {
      console.error('Error al obtener detalles de conversación:', error);
      setConversationWindows(windows => 
        windows.map(w => 
          w.id === windowId 
            ? { ...w, loadingMessages: false, messagesReady: true }
            : w
        )
      );
      return [];
    }
  }, [whatsAppServerPort, whatsAppApiPort, mlServerPort, scrollToBottom]);

  // Función para enviar respuesta - SIN CAMBIOS
  const sendReply = async (windowId: string) => {
    const window = conversationWindows.find(w => w.id === windowId);
    if (!window || (!window.replyMessage.trim() && window.attachedFiles.length === 0)) return;

    try {
      setConversationWindows(windows => 
        windows.map(w => 
          w.id === windowId 
            ? { ...w, sendingReply: true }
            : w
        )
      );
      
      const phoneNumber = window.conversationId;
      const API_TOKEN_KEY = getHatateToken();
      
      // Preparar mlStuff
      const mlStuffData = {
        whatsAppServerPort: whatsAppServerPort,
        whatsAppApiPort: whatsAppApiPort,
        mlServerPort: mlServerPort
      };
      
      // Si hay archivos adjuntos, usar FormData
      if (window.attachedFiles.length > 0) {
        const formData = new FormData();
        formData.append('to', phoneNumber);
        
        // Si no hay mensaje de texto, enviar un mensaje por defecto
        const messageToSend = window.replyMessage.trim() || '📎 Archivo adjunto';
        formData.append('message', messageToSend);
        
        // Agregar mlStuff como JSON string (el backend lo parseará)
        formData.append('mlStuff', JSON.stringify(mlStuffData));
        
        // Agregar archivos
        window.attachedFiles.forEach((attachedFile, index) => {
          formData.append('files', attachedFile.file);
        });
        
        console.log('📤 Sending message with files');

        const response = await axios.post(API_SEND_URL, formData, {
          headers: {
            'x-api-key': API_KEY,
            'Authorization': `${API_TOKEN_KEY}`
          }
        });
        
        if (response.data.success) {
          console.log('✅ Message with files sent successfully');
          setConversationWindows(windows => 
            windows.map(w => 
              w.id === windowId 
                ? { 
                    ...w, 
                    replyMessage: '', 
                    attachedFiles: [], 
                    autoScrollEnabled: true,
                    sendingReply: false 
                  }
                : w
            )
          );
          
          // Múltiples actualizaciones para asegurar que el mensaje aparezca
          setTimeout(() => fetchConversationDetail(window.conversationId, windowId, true), 500);
          setTimeout(() => fetchConversationDetail(window.conversationId, windowId, true), 2000);
          setTimeout(() => fetchConversationDetail(window.conversationId, windowId, true), 5000);
          setTimeout(() => scrollToBottom(windowId), 600);
        } else {
          console.error('❌ Error sending message with files:', response.data);
          alert(language === 'es' 
            ? 'Error al enviar el mensaje: ' + (response.data.message || 'Error desconocido')
            : 'Error sending message: ' + (response.data.message || 'Unknown error'));
        }
      } else {
        // Solo texto, usar JSON como espera el endpoint
        const jsonData = {
          to: phoneNumber, 
          message: window.replyMessage,
          mlStuff: mlStuffData
        };
        
        console.log('📤 Sending text message:', jsonData);

        const response = await axios.post(API_SEND_URL, jsonData, {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': API_KEY,
            'Authorization': `${API_TOKEN_KEY}`
          }
        });
        
        if (response.data.success) {
          console.log('✅ Text message sent successfully');
          setConversationWindows(windows => 
            windows.map(w => 
              w.id === windowId 
                ? { 
                    ...w, 
                    replyMessage: '', 
                    autoScrollEnabled: true,
                    sendingReply: false 
                  }
                : w
            )
          );
          
          setTimeout(() => fetchConversationDetail(window.conversationId, windowId, true), 500);
          setTimeout(() => fetchConversationDetail(window.conversationId, windowId, true), 2000);
          setTimeout(() => fetchConversationDetail(window.conversationId, windowId, true), 5000);
          setTimeout(() => scrollToBottom(windowId), 600);
        } else {
          console.error('❌ Error sending text message:', response.data);
          alert(language === 'es' 
            ? 'Error al enviar el mensaje: ' + (response.data.message || 'Error desconocido')
            : 'Error sending message: ' + (response.data.message || 'Unknown error'));
        }
      }
    } catch (error) {
      console.error('💥 Error in send API:', error);
      alert(language === 'es' 
        ? 'Error de conexión al enviar el mensaje. Intente nuevamente.'
        : 'Connection error when sending message. Please try again.');
    } finally {
      setConversationWindows(windows => 
        windows.map(w => 
          w.id === windowId 
            ? { ...w, sendingReply: false }
            : w
        )
      );
    }
  };

  // Funciones para manejar emojis y archivos - SIN CAMBIOS
  const handleEmojiSelect = (windowId: string, emoji: string) => {
    setConversationWindows(windows => 
      windows.map(w => 
        w.id === windowId 
          ? { 
              ...w, 
              replyMessage: w.replyMessage + emoji, 
              showEmojiPicker: false 
            }
          : w
      )
    );
  };

  const handleFileSelect = (windowId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const newFiles: AttachedFile[] = [];
    
    Array.from(files).forEach(file => {
      const fileType = file.type.startsWith('image/') ? 'image' : 
                      file.type.includes('pdf') || file.type.includes('document') ? 'document' : 
                      'other';
      
      const attachedFile: AttachedFile = {
        file,
        type: fileType
      };

      if (fileType === 'image') {
        const reader = new FileReader();
        reader.onload = (e) => {
          attachedFile.preview = e.target?.result as string;
          setConversationWindows(windows => 
            windows.map(w => 
              w.id === windowId 
                ? { ...w, attachedFiles: [...w.attachedFiles, attachedFile] }
                : w
            )
          );
        };
        reader.readAsDataURL(file);
      } else {
        newFiles.push(attachedFile);
      }
    });

    if (newFiles.length > 0) {
      setConversationWindows(windows => 
        windows.map(w => 
          w.id === windowId 
            ? { ...w, attachedFiles: [...w.attachedFiles, ...newFiles] }
            : w
        )
      );
    }

    if (event.target) {
      event.target.value = '';
    }
  };

  const removeAttachedFile = (windowId: string, index: number) => {
    setConversationWindows(windows => 
      windows.map(w => 
        w.id === windowId 
          ? { ...w, attachedFiles: w.attachedFiles.filter((_, i) => i !== index) }
          : w
      )
    );
  };

  // Funciones para modales de entrenamiento y eliminación - SIN CAMBIOS
  const showTrainingConfirmation = (conversationId: string) => {
    const conversation = conversations.find(c => c.id === conversationId);
    setTrainingMLConversationId(conversationId);
    setConversationNameToTrain(conversation ? getContactDisplayName(conversation) : conversationId);
    setTrainingMLResult(null);
    setTrainingModalVisible(true);
  };

  const closeTrainingModal = () => {
    setTrainingModalVisible(false);
    setTrainingMLConversationId(null);
    setConversationNameToTrain('');
    setTrainingMLResult(null);
  };

  const showDeleteConfirmation = (conversationId: string) => {
    const conversation = conversations.find(c => c.id === conversationId);
    setConversationToDelete(conversationId);
    setConversationNameToDelete(conversation ? getContactDisplayName(conversation) : conversationId);
    setDeleteModalVisible(true);
  };

  const cancelDelete = () => {
    setConversationToDelete(null);
    setConversationNameToDelete('');
    setDeleteModalVisible(false);
  };

  const handleDeleteChat = async () => {
    if (!conversationToDelete) return;
    
    try {
      setDeletingConversation(true);
      const API_TOKEN_KEY = getHatateToken();

      const response = await axios.post(`${API_BASE_URL}/conversation-delete?id=${conversationToDelete}`, 
        {},
        {
          headers: { 
            'x-api-key': API_KEY,
            'Authorization': `${API_TOKEN_KEY}`
          },
          data: {
            mlStuff: {
              whatsAppServerPort: whatsAppServerPort,
              whatsAppApiPort: whatsAppApiPort,
              mlServerPort: mlServerPort
            }
          }
        }
      );

      if (response.data.success) {
        fetchConversations(true);
        setDeleteModalVisible(false);
        setConversationToDelete(null);
        setConversationNameToDelete('');
        
        // Cerrar ventanas de esta conversación
        setConversationWindows(windows => 
          windows.filter(w => w.conversationId !== conversationToDelete)
        );
      } else {
        alert(language === 'es' 
          ? 'Error al eliminar el chat: ' + response.data.message 
          : 'Error deleting chat: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error al eliminar chat:', error);
      alert(language === 'es' 
        ? 'No se pudo eliminar el chat. Intente nuevamente.' 
        : 'Could not delete chat. Please try again.');
    } finally {
      setDeletingConversation(false);
    }
  };

  const trainMLWithConversation = async () => {
    if (!trainingMLConversationId) return;
    
    try {
      setTrainingMLInProgress(true);
      setTrainingMLResult(null);
      
      const response = await axios.post(`${API_BASE_URL}/train-ml-conversations`, 
        { conversationId: trainingMLConversationId },
        { 
          headers: { 
            'x-api-key': API_KEY,
            'Authorization': `${UserService.getStoredToken()}`
          },
          data: {
            mlStuff: {
              whatsAppServerPort: whatsAppServerPort,
              whatsAppApiPort: whatsAppApiPort,
              mlServerPort: mlServerPort
            }
          }
        }
      );
      
      if (response.data.success) {
        setTrainingMLResult({
          success: true,
          message: language === 'es' 
            ? `Entrenamiento exitoso: ${response.data.examples_added} ejemplos añadidos al ML` 
            : `Training successful: ${response.data.examples_added} examples added to ML`,
          examplesAdded: response.data.examples_added
        });
      } else {
        setTrainingMLResult({
          success: false,
          message: language === 'es' 
            ? 'Error en el entrenamiento: ' + response.data.message 
            : 'Training error: ' + response.data.message
        });
      }
    } catch (error) {
      console.error('Error al entrenar ML:', error);
      setTrainingMLResult({
        success: false,
        message: language === 'es' 
          ? 'Error al entrenar el ML con esta conversación' 
          : 'Error training ML with this conversation'
      });
    } finally {
      setTrainingMLInProgress(false);
    }
  };

  // MODIFICACIÓN: Configurar el intervalo de actualización con menor frecuencia
useEffect(() => {
    fetchConversations(false, false); // Carga inicial no silenciosa
    
    // Intervalo para actualizaciones silenciosas
    const silentUpdateInterval = setInterval(() => {
      console.log('Ejecutando actualización silenciosa...');
      fetchConversations(false, true); // Actualización silenciosa
    }, refreshInterval * 3); // Triplicamos el tiempo para actualizaciones silenciosas
    
    return () => {
      if (silentUpdateInterval) {
        clearInterval(silentUpdateInterval);
        console.log('Intervalo de actualización silenciosa limpiado');
      }
    };
  }, [refreshInterval, fetchConversations]);
  
  useEffect(() => {
    console.log('Parámetros críticos cambiados, refrescando datos...');
    fetchConversations(true, false); // Refresh forzado no silencioso
  }, [UserService.getStoredToken(), whatsAppServerPort, whatsAppApiPort, mlServerPort, fetchConversations]);

  // Formatear fecha
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(language === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Forzar actualización manual
  const handleRefresh = () => {
    fetchConversations(true);
  };

  // Generar páginas para la paginación
  const generatePaginationItems = () => {
    const items: React.ReactElement[] = [];
    for (let number = 1; number <= totalPages; number++) {
      items.push(
        <Pagination.Item 
          key={number} 
          active={number === currentPage} 
          onClick={() => setCurrentPage(number)}
        >
          {number}
        </Pagination.Item>
      );
    }
    return items;
  };

 return (
 <div className="conversations-layout" style={{ display: 'flex', height: '75vh', overflow: 'hidden' }}>
      {/* Panel lateral de conversaciones MEJORADO (30%) */}
      <div 
        className="conversations-sidebar" 
        style={{ 
          width: '36%', 
          borderRight: '1px solid #dee2e6', 
          backgroundColor: '#fff', 
          display: conversationsListWindow.isVisible ? 'none' : 'flex', 
          flexDirection: 'column' 
        }}
      >
        <Card className="h-100" style={{ border: 'none', borderRadius: 0 }}>
          <Card.Header className="d-flex justify-content-between align-items-center" style={{ padding: '0.75rem', borderBottom: '1px solid #dee2e6' }}>
            <div className="d-flex align-items-center">
              <h6 className="mb-0" style={{ fontSize: '14px', fontWeight: '600' }}>
                {language === 'es' ? 'Conversaciones' : 'Conversations'}
                {conversations.length > 0 && (
                  <Badge bg="primary" className="ms-2" style={{ fontSize: '10px' }}>
                    {conversations.length}
                  </Badge>
                )}
              </h6>
            </div>
            <div className="d-flex align-items-center gap-1">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={openConversationsListWindow}
                disabled={loading && conversations.length === 0}
                style={{ fontSize: '10px', padding: '4px 8px' }}
                title={language === 'es' ? 'Vista Modal' : 'Modal View'}
              >
                <FontAwesomeIcon icon={faExpand} />
              </Button>
              <Button 
                variant="outline-secondary" 
                size="sm"
                onClick={() => fetchConversations(true, false)} // Forzar refresh no silencioso
                disabled={loading}
                style={{ fontSize: '10px', padding: '4px 8px' }}
                title={language === 'es' ? 'Actualizar' : 'Refresh'}
              >
                {loading ? (
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                ) : (
                  <i className="fas fa-sync-alt"></i>
                )}
              </Button>
            </div>
          </Card.Header>

          <Card.Body style={{ padding: '0.5rem', flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {loading && conversations.length === 0 ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" />
                <p className="mt-2 small">{language === 'es' ? 'Cargando...' : 'Loading...'}</p>
              </div>
            ) : error && conversations.length === 0 ? (
              <div className="alert alert-danger p-2 m-0">
                <small>{error}</small>
                <Button 
                  variant="outline-danger" 
                  size="sm" 
                  className="ms-2" 
                  onClick={() => fetchConversations(true, false)}
                  style={{ fontSize: '9px', padding: '2px 6px' }}
                >
                  {language === 'es' ? 'Reintentar' : 'Retry'}
                </Button>
              </div>
            ) : conversations.length === 0 && !loading ? (
              <div className="alert alert-info p-2 m-0">
                <small>
                  {language === 'es' 
                    ? 'No hay conversaciones disponibles.' 
                    : 'No conversations available.'}
                </small>
              </div>
            ) : (
              <>
                {/* Barra de búsqueda compacta */}
                <div className="mb-2">
                  <Form.Control
                    type="text"
                    placeholder={language === 'es' ? 'Buscar...' : 'Search...'}
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    size="sm"
                    style={{ fontSize: '12px' }}
                  />
                </div>
                
                {/* Lista compacta de conversaciones MEJORADA */}
                <div className="conversation-list" style={{ flex: 1, overflowY: 'auto' }}>
                  {paginatedConversations.map((conversation) => (
                    <div 
                      key={conversation.id} 
                      className="conversation-item-improved"
                      style={{
                        padding: '10px',
                        //borderBottom: '1px solid #f0f0f0',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontSize: '12px',
                        borderRadius: '6px',
                        marginBottom: '4px'
                      }}
                      onClick={() => handleConversationClick(conversation.id)}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      {/* Header con contacto y estado */}
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center" style={{ flex: 1, minWidth: 0 }}>
                          {/* Indicador de estado más grande */}
                          <div 
                            style={{
                              width: '10px',
                              height: '10px',
                              borderRadius: '50%',
                              backgroundColor: conversation.isActive ? '#28a745' : '#ffc107',
                              marginRight: '8px',
                              flexShrink: 0,
                              border: '2px solid white',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                            }}
                            title={conversation.isActive 
                              ? (language === 'es' ? 'Activo' : 'Active')
                              : (language === 'es' ? 'Inactivo' : 'Inactive')
                            }
                          />
                          <span 
                            className="text-truncate" 
                            style={{ fontWeight: '600', fontSize: '13px', color: '#2c3e50' }}
                            title={getContactDisplayName(conversation)}
                          >
                            {getContactDisplayName(conversation)}
                          </span>
                        </div>
                        <span 
                          className="badge bg-secondary" 
                          style={{ fontSize: '9px', marginLeft: '6px', borderRadius: '12px' }}
                        >
                          {conversation.messageCount}
                        </span>
                      </div>

                      {/* Preview del mensaje */}
                      <div className="mb-2">
                        <span 
                          className="text-muted text-truncate d-block" 
                          style={{ fontSize: '11px', lineHeight: '1.3' }}
                          title={conversation.preview}
                        >
                          {conversation.preview.length > 50 
                            ? conversation.preview.substring(0, 50) + '...' 
                            : conversation.preview}
                        </span>
                      </div>

                      {/* NUEVA: Pestaña de acciones mejorada */}
                      <div className="d-flex justify-content-between align-items-center" onClick={(e) => e.stopPropagation()}>
                        <small className="text-muted" style={{ fontSize: '10px' }}>
                          {formatDate(conversation.lastMessageTime).split(' ')[1]}
                        </small>
                        
                        <Dropdown>
                          <Dropdown.Toggle 
                            variant="outline-secondary" 
                            size="sm" 
                            id={`dropdown-${conversation.id}`}
                            style={{ 
                              fontSize: '10px', 
                              padding: '2px 6px',
                              border: 'none',
                              backgroundColor: 'transparent'
                            }}
                          >
                            <FontAwesomeIcon icon={faEllipsisV} />
                          </Dropdown.Toggle>

                          <Dropdown.Menu>
                            <Dropdown.Item 
                              onClick={(e) => {
                                e.stopPropagation();
                                openConversationWindow(conversation.id);
                              }}
                              style={{ fontSize: '12px', padding: '6px 12px' }}
                            >
                              <FontAwesomeIcon icon={faEye} className="me-2" />
                              {language === 'es' ? 'Ver Chat' : 'View Chat'}
                            </Dropdown.Item>
                            <Dropdown.Item 
                              onClick={(e) => {
                                e.stopPropagation();
                                showTrainingConfirmation(conversation.id);
                              }}
                              style={{ fontSize: '12px', padding: '6px 12px' }}
                            >
                              <FontAwesomeIcon icon={faUserGraduate} className="me-2" />
                              {language === 'es' ? 'Entrenar ML' : 'Train ML'}
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item 
                              onClick={(e) => {
                                e.stopPropagation();
                                showDeleteConfirmation(conversation.id);
                              }}
                              style={{ fontSize: '12px', padding: '6px 12px', color: '#dc3545' }}
                            >
                              <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                              {language === 'es' ? 'Eliminar' : 'Delete'}
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Paginación compacta */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center mt-2">
                    <Pagination size="sm" style={{ fontSize: '10px' }}>
                      <Pagination.Prev 
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} 
                        disabled={currentPage === 1} 
                        style={{ fontSize: '10px' }}
                      />
                      <Pagination.Item active style={{ fontSize: '10px' }}>
                        {currentPage}
                      </Pagination.Item>
                      <Pagination.Next 
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} 
                        disabled={currentPage === totalPages} 
                        style={{ fontSize: '10px' }}
                      />
                    </Pagination>
                  </div>
                )}

                {/* Información de actualización */}
                <div className="mt-2">
                  <small className="text-muted" style={{ fontSize: '9px' }}>
                    {language === 'es' 
                      ? `Actualizado: ${formatDate(lastUpdate.toISOString()).split(' ')[1]}`
                      : `Updated: ${formatDate(lastUpdate.toISOString()).split(' ')[1]}`}
                  </small>
                </div>
              </>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* NUEVA: Área de trabajo mejorada (70%) */}
      <div 
        className="conversations-work-area bg-body" 
        style={{ 
          width: conversationsListWindow.isVisible ? '100%' : '70%', 
          position: 'relative', 
          //backgroundColor: '#f8f9fa',
          //background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          background: 'linear-gradient(135deg, --var(--bg-body) 0%, --var(--bg-body) 100%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}
      >
        {/* NUEVA: Área de bienvenida cuando no hay ventanas */}
        {conversationWindows.filter(w => !w.isMinimized).length === 0 && !conversationsListWindow.isVisible && (
          <div className="welcome-area text-center bg-body-emphasis" style={{ color: 'white', maxWidth: '500px' }}>
            <div style={{ fontSize: '4rem', marginBottom: '20px', opacity: 0.8 }}>
              💬
            </div>
            <h3 style={{ fontWeight: '300', marginBottom: '15px' }}>
              {language === 'es' 
                ? 'Gestión Inteligente de Conversaciones' 
                : 'Smart Conversation Management'}
            </h3>
            <p className="text-success mb-2" >
              {language === 'es' 
                ? 'Haz clic en cualquier conversación para abrir una ventana flotante y gestionar múltiples chats simultáneamente.'
                : 'Click on any conversation to open a floating window and manage multiple chats simultaneously.'}
            </p>
            <div className="mt-4">
              {/*<Button 
                variant="light" 
                size="lg" 
                onClick={openConversationsListWindow}
                style={{ 
                  borderRadius: '25px', 
                  padding: '12px 30px',
                  fontWeight: '500',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
                }}
              >
                <FontAwesomeIcon icon={faExpand} className="me-2" />
                {language === 'es' ? 'Ver Lista Completa' : 'View Full List'}
              </Button>*/}
            </div>
          </div>
        )}

        {/* Información de ventanas activas */}
        {conversationWindows.filter(w => !w.isMinimized).length > 0 && (
          <div 
            className="active-windows-info"
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(255,255,255,0.9)',
              padding: '10px 15px',
              borderRadius: '20px',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}
          >
            <small style={{ fontWeight: '500', color: '#333' }}>
              {language === 'es' 
                ? `${conversationWindows.filter(w => !w.isMinimized).length} ventana(s) activa(s)`
                : `${conversationWindows.filter(w => !w.isMinimized).length} active window(s)`}
            </small>
          </div>
        )}
      </div>
      {/* Ventana modal de lista de conversaciones */}
        {conversationsListWindow.isVisible && (
          <>
            {conversationsListWindow.isMinimized ? (
              // Burbuja minimizada
              <div
                className="conversation-bubble"
                style={{
                  position: 'fixed',
                  left: `${conversationsListWindow.position.x}px`,
                  top: `${conversationsListWindow.position.y}px`,
                  zIndex: conversationsListWindow.zIndex,
                  width: '60px',
                  height: '60px',
                  backgroundColor: '#28a745',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  border: '2px solid white',
                  touchAction: 'none'
                }}
                onMouseDown={(e) => startDragConversationsList(e)}
                onTouchStart={(e) => startDragConversationsList(e)}
                onMouseUp={(e) => {
                  const { wasDragging, dragDuration } = endDrag();
                  if (!wasDragging && dragDuration < 200) {
                    restoreConversationsListWindow();
                  }
                }}
                onTouchEnd={(e) => {
                  const { wasDragging, dragDuration } = endDrag();
                  if (!wasDragging && dragDuration < 200) {
                    restoreConversationsListWindow();
                  }
                }}
                title={language === 'es' ? 'Lista de Conversaciones' : 'Conversations List'}
              >
                <FontAwesomeIcon 
                  icon={faList} 
                  color="white" 
                  size="lg" 
                />
              </div>
            ) : (
              // Ventana modal completa - IGUAL QUE ANTES
              <div
                id="conversations-list-window"
                className="conversation-window"
                style={{
                  position: 'fixed',
                  left: `${conversationsListWindow.position.x}px`,
                  top: `${conversationsListWindow.position.y}px`,
                  width: `${conversationsListWindow.size.width}px`,
                  height: `${conversationsListWindow.size.height}px`,
                  zIndex: conversationsListWindow.zIndex,
                  backgroundColor: 'white',
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden'
                }}
                onMouseDown={() => bringConversationsListToFront()}
              >
                {/* Header - igual que antes pero con título actualizado */}
                <div
                  className="conversation-window-header"
                  style={{
                    padding: '12px 16px',
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #dee2e6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'move',
                    touchAction: 'none'
                  }}
                  onMouseDown={(e) => startDragConversationsList(e)}
                  onTouchStart={(e) => startDragConversationsList(e)}
                >
                  {/* Resto del contenido del header igual que antes */}
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FontAwesomeIcon icon={faGripVertical} className="me-2 text-muted" />
                    <FontAwesomeIcon icon={faList} className="me-2 text-primary" />
                    <span style={{ fontSize: '14px', fontWeight: '600' }}>
                      {language === 'es' ? 'Vista Modal - Conversaciones' : 'Modal View - Conversations'}
                    </span>
                    {conversations.length > 0 && (
                      <Badge bg="primary" className="ms-2">
                        {conversations.length}
                      </Badge>
                    )}
                  </div>
                  <div>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 me-2"
                      onClick={() => minimizeConversationsListWindow()}
                      style={{ fontSize: '12px' }}
                    >
                      <FontAwesomeIcon icon={faMinus} />
                    </Button>
                    <Button
                      variant="link"
                      size="sm" 
                      className="p-0"
                      onClick={() => closeConversationsListWindow()}
                      style={{ fontSize: '12px', color: '#dc3545' }}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </div>
                </div>

                {/* Cuerpo - igual que antes */}
                <div className="conversation-window-body" style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    background: '#fafafa'
                  }}>
                  {/* Contenido de la tabla igual que antes */}
                  <div className="row mb-3">
                    <div className="col-8">
                      <Form.Control
                        type="text"
                        placeholder={language === 'es' ? 'Buscar conversaciones...' : 'Search conversations...'}
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1);
                        }}
                        size="sm"
                      />
                    </div>
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      <Form.Select 
                        size="sm"
                        value={itemsPerPage} 
                        onChange={handleItemsPerPageChange}
                        style={{ width: '80px' }}
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={15}>15</option>
                        <option value={20}>20</option>
                      </Form.Select>
                    </div>
                  </div>
                  
                  {/* Tabla - IGUAL QUE ANTES pero con nombres mejorados */}
                  <div className="table-responsive flex-grow-1">
                    <Table striped bordered hover size="sm">
                      <thead>
                        <tr>
                          <th style={{ fontSize: '12px' }}>
                            {language === 'es' ? 'Contacto/Grupo' : 'Contact/Group'}
                          </th>
                          <th style={{ fontSize: '12px' }}>
                            {language === 'es' ? 'Última actividad' : 'Last activity'}
                          </th>
                          <th style={{ fontSize: '12px' }}>
                            {language ==='es' ? 'Mensajes' : 'Messages'}
                          </th>
                          <th style={{ fontSize: '12px' }}>
                            {language === 'es' ? 'Vista previa' : 'Preview'}
                          </th>
                          <th style={{ fontSize: '12px' }}>
                            {language === 'es' ? 'Estado' : 'Status'}
                          </th>
                          <th style={{ fontSize: '12px' }}>
                            {language === 'es' ? 'Acciones' : 'Actions'}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedConversations.map((conversation) => (
                          <tr key={conversation.id}>
                            <td style={{ fontSize: '11px' }}>
                              <span title={conversation.id}>
                                {getContactDisplayName(conversation)}
                              </span>
                            </td>
                            <td style={{ fontSize: '11px' }}>
                              {formatDate(conversation.lastMessageTime)}
                            </td>
                            <td style={{ fontSize: '11px' }}>
                              {conversation.messageCount}
                            </td>
                            <td style={{ fontSize: '10px', maxWidth: '150px' }}>
                              <span className="text-truncate d-block">
                                {conversation.preview}
                              </span>
                            </td>
                            <td>
                              {conversation.isActive ? (
                                <Badge bg="success" style={{ fontSize: '9px' }}>
                                  {language === 'es' ? 'Activo' : 'Active'}
                                </Badge>
                              ) : (
                                <Badge bg="warning" style={{ fontSize: '9px' }}>
                                  {language === 'es' ? 'Inactivo' : 'Inactive'}
                                </Badge>
                              )}
                            </td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => openConversationWindow(conversation.id)}
                                className="me-1"
                                style={{ padding: '2px 6px', fontSize: '10px' }}
                                title={language === 'es' ? 'Ver conversación' : 'View conversation'}
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </Button>
                              <Button
                                variant="outline-success"
                                size="sm"
                                className="me-1"
                                onClick={() => showTrainingConfirmation(conversation.id)}
                                style={{ padding: '2px 6px', fontSize: '10px' }}
                                title={language === 'es' ? 'Entrenar ML' : 'Train ML'}
                              >
                                <FontAwesomeIcon icon={faUserGraduate} />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => showDeleteConfirmation(conversation.id)}
                                style={{ padding: '2px 6px', fontSize: '10px' }}
                                title={language === 'es' ? 'Eliminar conversación' : 'Delete conversation'}
                              >
                                <FontAwesomeIcon icon={faTrashAlt} />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  {/* Paginación y footer igual que antes */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-3">
                      <Pagination size="sm">
                        <Pagination.First 
                          onClick={() => setCurrentPage(1)} 
                          disabled={currentPage === 1} 
                        />
                        <Pagination.Prev 
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} 
                          disabled={currentPage === 1} 
                        />
                        
                        {generatePaginationItems()}
                        
                        <Pagination.Next 
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} 
                          disabled={currentPage === totalPages} 
                        />
                        <Pagination.Last 
                          onClick={() => setCurrentPage(totalPages)} 
                          disabled={currentPage === totalPages} 
                        />
                      </Pagination>
                    </div>
                  )}

                  <div className="mt-3 d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      {language === 'es' 
                        ? `Última actualización: ${formatDate(lastUpdate.toISOString())}`
                        : `Last update: ${formatDate(lastUpdate.toISOString())}`}
                    </small>
                    <Button 
                      variant="outline-secondary" 
                      size="sm"
                      onClick={() => fetchConversations(true, false)}
                      disabled={loading}
                      style={{ fontSize: '10px' }}
                    >
                      {loading ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                          <span className="ms-1">{language === 'es' ? 'Actualizando...' : 'Refreshing...'}</span>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-sync-alt me-1"></i>
                          {language === 'es' ? 'Actualizar' : 'Refresh'}
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Handle de resize */}
                <div
                  className="resize-handle"
                  style={{
                    position: 'absolute',
                    bottom: '0',
                    right: '0',
                    width: '15px',
                    height: '15px',
                    cursor: 'nw-resize',
                    background: 'linear-gradient(-45deg, transparent 0%, transparent 30%, #ccc 30%, #ccc 40%, transparent 40%, transparent 60%, #ccc 60%, #ccc 70%, transparent 70%)'
                  }}
                  onMouseDown={(e) => startResizeConversationsList(e)}
                />
              </div>
            )}
          </>
        )}

        {/* NUEVAS: Ventanas de conversación individuales con EDITOR DE NOMBRES Y NOMBRES EN MENSAJES */}
        {conversationWindows.map((conversationWindow) => {
          const conversation = conversations.find(c => c.id === conversationWindow.conversationId);
          const conversationName = conversation ? getContactDisplayName(conversation) : conversationWindow.conversationId;

          if (conversationWindow.isMinimized) {
            // Burbuja minimizada
            const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
            
            return (
              <div
                key={conversationWindow.id}
                className="conversation-bubble"
                style={{
                  position: 'fixed',
                  left: `${conversationWindow.position.x}px`,
                  top: `${conversationWindow.position.y}px`,
                  zIndex: conversationWindow.zIndex,
                  width: isMobile ? '50px' : '60px',
                  height: isMobile ? '50px' : '60px',
                  backgroundColor: conversationWindow.isAutoUpdateEnabled ? '#007bff' : '#6c757d',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  border: '2px solid white',
                  touchAction: 'none'
                }}
                onMouseDown={(e) => startDrag(conversationWindow.id, e)}
                onTouchStart={(e) => startDrag(conversationWindow.id, e)}
                onMouseUp={(e) => {
                  const { wasDragging, dragDuration } = endDrag();
                  if (!wasDragging && dragDuration < 200) {
                    restoreConversationWindow(conversationWindow.id);
                  }
                }}
                onTouchEnd={(e) => {
                  const { wasDragging, dragDuration } = endDrag();
                  if (!wasDragging && dragDuration < 200) {
                    restoreConversationWindow(conversationWindow.id);
                  }
                }}
                title={`${conversationName} - ${conversationWindow.isAutoUpdateEnabled ? 
                  (language === 'es' ? 'Auto-actualización ON' : 'Auto-update ON') : 
                  (language === 'es' ? 'Auto-actualización OFF' : 'Auto-update OFF')}`}
              >
                <FontAwesomeIcon 
                  icon={faEye} 
                  color="white" 
                  size={isMobile ? "sm" : "lg"} 
                />
              </div>
            );
          }

          // NUEVA: Ventana completa de conversación con EDITOR DE NOMBRES Y NOMBRES EN MENSAJES
          return (
            <div
              key={conversationWindow.id}
              id={conversationWindow.id}
              className="conversation-window bg-body"
              style={{
                position: 'fixed',
                left: `${conversationWindow.position.x}px`,
                top: `${conversationWindow.position.y}px`,
                width: `${conversationWindow.size.width}px`,
                height: `${conversationWindow.size.height}px`,
                zIndex: conversationWindow.zIndex,
                border: '1px solid rgba(221, 221, 221, 0.35)',
                borderRadius: '8px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
              onMouseDown={() => bringToFront(conversationWindow.id)}
            >
              {/* NUEVO: Header con editor de nombres */}
              <div
                className="conversation-window-header bg-body"
                style={{
                  padding: (typeof window !== 'undefined' && window.innerWidth <= 768) ? '12px 8px' : '8px 12px',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.26)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: conversationWindow.isEditingName ? 'default' : 'move',
                  touchAction: 'none',
                  minHeight: (typeof window !== 'undefined' && window.innerWidth <= 768) ? '44px' : 'auto'
                }}
                onMouseDown={(e) => {
                  if (!conversationWindow.isEditingName) {
                    startDrag(conversationWindow.id, e);
                  }
                }}
                onTouchStart={(e) => {
                  if (!conversationWindow.isEditingName) {
                    startDrag(conversationWindow.id, e);
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                  <FontAwesomeIcon icon={faGripVertical} className="me-2 text-muted" />
                  
                  {/* Indicador de estado activo/inactivo */}
                  <div 
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: conversation?.isActive ? '#28a745' : '#fd7e14',
                      marginRight: '8px',
                      border: '1px solid rgba(0,0,0,0.1)'
                    }}
                    title={conversation?.isActive 
                      ? (language === 'es' ? 'Activo' : 'Active')
                      : (language === 'es' ? 'Inactivo' : 'Inactive')
                    }
                  />
                  
                  {/* NUEVO: Campo editable de nombre */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {conversationWindow.isEditingName ? (
                      <Form.Control
                        type="text"
                        value={conversationWindow.editingName}
                        onChange={(e) => setConversationWindows(windows => 
                          windows.map(w => 
                            w.id === conversationWindow.id 
                              ? { ...w, editingName: e.target.value }
                              : w
                          )
                        )}
                        onKeyDown={(e) => handleEditingNameKeyPress(conversationWindow.id, e)}
                        onBlur={() => saveEditingName(conversationWindow.id)}
                        autoFocus
                        size="sm"
                        style={{ 
                          fontSize: '13px', 
                          fontWeight: '600',
                          border: '1px solid #007bff',
                          borderRadius: '4px'
                        }}
                        disabled={conversationWindow.savingName}
                        placeholder={language === 'es' ? 'Nombre del contacto...' : 'Contact name...'}
                      />
                    ) : (
                      <div className="d-flex align-items-center">
                        <span 
                          style={{ 
                            fontSize: '14px', 
                            fontWeight: '600',
                            cursor: 'text',
                            flex: 1,
                            minWidth: 0,
                            maxWidth: 100,
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap'
                          }}
                          title={conversationName}
                        >
                          {conversationName}
                        </span>
                        
                        {/* NUEVO: Botón de editar nombre */}
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 ms-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditingName(conversationWindow.id);
                          }}
                          style={{ 
                            fontSize: '12px',
                            color: '#6c757d',
                            textDecoration: 'none'
                          }}
                          title={language === 'es' ? 'Editar nombre' : 'Edit name'}
                        >
                          {conversationWindow.savingName ? (
                            <Spinner as="span" animation="border" size="sm" />
                          ) : (
                            <FontAwesomeIcon icon={faEdit} />
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {/* Botón de toggle auto-actualización */}
                  <Button
                    variant={conversationWindow.isAutoUpdateEnabled ? "success" : "outline-secondary"}
                    size="sm"
                    className="me-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleWindowAutoUpdate(conversationWindow.id);
                    }}
                    style={{ 
                      fontSize: '10px', 
                      padding: '2px 6px',
                      border: 'none',
                      backgroundColor: conversationWindow.isAutoUpdateEnabled ? '#28a745' : '#6c757d',
                      color: 'white'
                    }}
                    title={conversationWindow.isAutoUpdateEnabled 
                      ? (language === 'es' ? 'Desactivar auto-actualización' : 'Disable auto-update')
                      : (language === 'es' ? 'Activar auto-actualización' : 'Enable auto-update')
                    }
                  >
                    <div 
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%'
                      }}
                    />
                  </Button>
                  
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 me-2"
                    onClick={() => minimizeConversationWindow(conversationWindow.id)}
                    style={{ fontSize: '12px' }}
                  >
                    <FontAwesomeIcon icon={faMinus} />
                  </Button>
                  <Button
                    variant="link"
                    size="sm" 
                    className="p-0"
                    onClick={() => closeConversationWindow(conversationWindow.id)}
                    style={{ fontSize: '12px', color: '#dc3545' }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                </div>
              </div>
              
              {/* Cuerpo de la ventana - Mensajes CON NOMBRES MEJORADOS */}
              <div
                className="conversation-window-body bg-body"
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  //background: '#f8f9fa'
                }}
              >
                {conversationWindow.loadingMessages || conversationWindow.isInitialLoad ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" size="sm" />
                    <p className="mt-2 small">{language === 'es' ? 'Cargando...' : 'Loading...'}</p>
                  </div>
                ) : conversationWindow.displayedMessages.length === 0 ? (
                  <div className="alert alert-info small">
                    {language === 'es' 
                      ? 'No hay mensajes disponibles.' 
                      : 'No messages available.'}
                  </div>
                ) : (
                  <div className="messages-container" style={{ 
                    flex: 1, 
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                  }}>
                    {conversationWindow.displayedMessages.map((message, index) => {
                      // NUEVO: Obtener información del remitente
                      const senderInfo = getMessageSenderInfo(message, conversationWindow.conversationId);
                      
                      return (
                        <Card 
                          key={message.id || `msg-${index}`} 
                          className={`message-card ${message.sender === 'bot' ? 'ms-auto bot-message' : 'me-auto user-message'}`}
                          style={{ 
                            maxWidth: '80%', 
                            fontSize: '12px',
                            border: 'none',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            marginBottom: '2px'
                          }}
                        >
                          <Card.Body className="py-1 px-2">
                            <div className="mb-1">
                              <small className="text-muted" style={{ fontSize: '10px' }}>
                                <strong>{senderInfo.displayName}</strong>
                                {senderInfo.phoneNumber && senderInfo.displayName !== senderInfo.phoneNumber && (
                                  <span className="text-muted"> ({senderInfo.phoneNumber})</span>
                                )}
                                {' - '}
                                {formatDate(message.timestamp)}
                              </small>
                            </div>
                            <div className="message-text" style={{ fontSize: '12px', wordWrap: 'break-word' }}>
                              {message.text || 'Sin contenido'}
                            </div>
                          </Card.Body>
                        </Card>
                      );
                    })}
                    {/* Elemento para scroll automático */}
                    <div 
                      ref={(el) => { messagesEndRefs.current[conversationWindow.id] = el; }}
                      style={{ height: '1px' }}
                    />
                  </div>
                )}
              </div>
              {/* Footer de la ventana - Input */}
              <div
                className="conversation-window-footer bg-body"
                style={{
                  padding: '6px',
                  //borderTop: '1px solid #dee2e6'
                }}
              >
                {/* Preview de archivos adjuntos */}
                {conversationWindow.attachedFiles.length > 0 && (
                  <div className="attached-files-preview mb-2">
                    <div className="d-flex flex-wrap gap-1">
                      {conversationWindow.attachedFiles.map((attachedFile, index) => (
                        <div key={index} className="attached-file-item position-relative">
                          {attachedFile.type === 'image' && attachedFile.preview ? (
                            <div className="image-preview">
                              <img 
                                src={attachedFile.preview} 
                                alt={attachedFile.file.name}
                                style={{ width: '30px', height: '30px', objectFit: 'cover', borderRadius: '4px' }}
                              />
                              <Button
                                variant="danger"
                                size="sm"
                                className="position-absolute top-0 end-0 rounded-circle"
                                style={{ width: '15px', height: '15px', padding: '0', fontSize: '8px' }}
                                onClick={() => removeAttachedFile(conversationWindow.id, index)}
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </Button>
                            </div>
                          ) : (
                            <div className="file-preview d-flex align-items-center bg-body p-0 rounded">
                              <FontAwesomeIcon icon={faPaperclip} style={{ fontSize: '10px' }} className="me-1" />
                              <span className="small text-truncate" style={{ maxWidth: '50px', fontSize: '10px' }}>
                                {attachedFile.file.name}
                              </span>
                              <Button
                                variant="danger"
                                size="sm"
                                className="ms-1 rounded-circle"
                                style={{ width: '15px', height: '15px', padding: '0', fontSize: '8px' }}
                                onClick={() => removeAttachedFile(conversationWindow.id, index)}
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Selector de emojis */}
                {conversationWindow.showEmojiPicker && (
                  <div className="emoji-picker mb-2 p-1 border rounded bg-body" style={{ maxHeight: '100px', overflowY: 'auto' }}>
                    <div className="emoji-categories">
                      {Object.entries(EMOJI_CATEGORIES).map(([category, emojis]) => (
                        <div key={category} className="emoji-category mb-1">
                          <small className="text-muted text-capitalize" style={{ fontSize: '9px' }}>{category}</small>
                          <div className="emoji-grid d-flex flex-wrap gap-1">
                            {emojis.slice(0, 10).map((emoji, index) => (
                              <button
                                key={index}
                                type="button"
                                className="btn btn-sm btn-outline-light emoji-btn"
                                onClick={() => handleEmojiSelect(conversationWindow.id, emoji)}
                                style={{ 
                                  border: 'none', 
                                  fontSize: '12px', 
                                  padding: '1px 3px',
                                  background: 'transparent'
                                }}
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Form onSubmit={(e) => { e.preventDefault(); sendReply(conversationWindow.id); }}>
                  <InputGroup size="sm">
                    {/* Input oculto para archivos */}
                    <input
                      type="file"
                      ref={(el) => { fileInputRefs.current[conversationWindow.id] = el; }}
                      onChange={(e) => handleFileSelect(conversationWindow.id, e)}
                      multiple
                      accept="image/*,application/pdf,.doc,.docx,.txt"
                      style={{ display: 'none' }}
                    />

                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>{language === 'es' ? 'Archivo' : 'File'}</Tooltip>}
                    >
                      <Button 
                        variant="outline-tertiary"
                        size="sm"
                        onClick={() => fileInputRefs.current[conversationWindow.id]?.click()}
                        disabled={conversationWindow.sendingReply || conversationWindow.loadingMessages}
                      >
                        <FontAwesomeIcon icon={faPaperclip} style={{ fontSize: '10px' }} />
                      </Button>
                    </OverlayTrigger>

                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>{language === 'es' ? 'Emojis' : 'Emojis'}</Tooltip>}
                    >
                      <Button 
                        variant="outline-tertiary"
                        size="sm"
                        onClick={() => setConversationWindows(windows => 
                          windows.map(w => 
                            w.id === conversationWindow.id 
                              ? { ...w, showEmojiPicker: !w.showEmojiPicker }
                              : w
                          )
                        )}
                        disabled={conversationWindow.sendingReply || conversationWindow.loadingMessages}
                        >
                        <FontAwesomeIcon icon={faSmile} style={{ fontSize: '10px' }} />
                      </Button>
                    </OverlayTrigger>

                    <Form.Control
                      type="text"
                      placeholder={language === 'es' ? 'Mensaje...' : 'Message...'}
                      value={conversationWindow.replyMessage}
                      onChange={(e) => setConversationWindows(windows => 
                        windows.map(w => 
                          w.id === conversationWindow.id 
                            ? { ...w, replyMessage: e.target.value }
                            : w
                        )
                      )}
                      disabled={conversationWindow.sendingReply || conversationWindow.loadingMessages}
                      autoComplete="off"
                      style={{ fontSize: '12px' }}
                    />
                    <Button 
                      variant="primary" 
                      size="sm"
                      type="submit"
                      disabled={conversationWindow.sendingReply || conversationWindow.loadingMessages || (!conversationWindow.replyMessage.trim() && conversationWindow.attachedFiles.length === 0)}
                    >
                      {conversationWindow.sendingReply ? (
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                      ) : (
                        <i className="fas fa-paper-plane" style={{ fontSize: '10px' }}></i>
                      )}
                    </Button>
                  </InputGroup>
                </Form>
              </div>

              {/* Handle de resize */}
              <div
                className="resize-handle"
                style={{
                  position: 'absolute',
                  bottom: '0',
                  right: '0',
                  width: '15px',
                  height: '15px',
                  cursor: 'nw-resize',
                  background: 'linear-gradient(-45deg, transparent 0%, transparent 30%, #ccc 30%, #ccc 40%, transparent 40%, transparent 60%, #ccc 60%, #ccc 70%, transparent 70%)'
                }}
                onMouseDown={(e) => startResize(conversationWindow.id, e)}
              />
            </div>
          );
        })}

      {/* Modal de confirmación de entrenamiento ML */}
      {trainingModalVisible && (
        <div 
          className="modal show d-block"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 9999
          }}
          onClick={closeTrainingModal}
        >
          <div 
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {language === 'es' ? 'Entrenamiento ML' : 'ML Training'}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={closeTrainingModal}
                ></button>
              </div>
              <div className="modal-body">
                {trainingMLResult ? (
                  <div className={`alert ${trainingMLResult.success ? 'alert-success' : 'alert-danger'}`}>
                    {trainingMLResult.message}
                  </div>
                ) : (
                  language === 'es' 
                    ? `¿Desea entrenar el modelo ML con la conversación de "${conversationNameToTrain}"?`
                    : `Do you want to train the ML model with the conversation from "${conversationNameToTrain}"?`
                )}
              </div>
              <div className="modal-footer">
                {trainingMLResult ? (
                  <Button variant="primary" onClick={closeTrainingModal}>
                    {language === 'es' ? 'Aceptar' : 'OK'}
                  </Button>
                ) : (
                  <>
                    <Button variant="secondary" onClick={closeTrainingModal}>
                      {language === 'es' ? 'Cancelar' : 'Cancel'}
                    </Button>
                    <Button 
                      variant="primary"
                      onClick={trainMLWithConversation}
                      disabled={trainingMLInProgress}
                    >
                      {trainingMLInProgress ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                          <span className="ms-2">{language === 'es' ? 'Entrenando...' : 'Training...'}</span>
                        </>
                      ) : (
                        language === 'es' ? 'Entrenar' : 'Train'
                      )}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {deleteModalVisible && (
        <div 
          className="modal show d-block"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 9999
          }}
          onClick={cancelDelete}
        >
          <div 
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {language === 'es' ? 'Confirmar eliminación' : 'Confirm Deletion'}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={cancelDelete}
                ></button>
              </div>
              <div className="modal-body">
                {language === 'es' 
                  ? `¿Está seguro de que desea eliminar la conversación con "${conversationNameToDelete}"? Esta acción no se puede deshacer.`
                  : `Are you sure you want to delete the conversation with "${conversationNameToDelete}"? This action cannot be undone.`}
              </div>
              <div className="modal-footer">
                <Button variant="secondary" onClick={cancelDelete}>
                  {language === 'es' ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button 
                  variant="danger" 
                  onClick={handleDeleteChat}
                  disabled={deletingConversation}
                >
                  {deletingConversation ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                      <span className="ms-2">{language === 'es' ? 'Eliminando...' : 'Deleting...'}</span>
                    </>
                  ) : (
                    language === 'es' ? 'Eliminar' : 'Delete'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* NUEVOS: Estilos CSS mejorados */}
      <style>{`
        /* Layout mejorado */
        .conversations-layout {
          font-family: system-ui, -apple-system, sans-serif;
        }

        .conversations-sidebar {
          min-width: 250px;
          max-width: 400px;
        }

        /* NUEVOS: Estilos mejorados para items de conversación */
        .conversation-item-improved {
          border: 1px solid transparent;
          //background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
          box-shadow: 0 2px 4px rgba(0,0,0,0.04);
          transition: all 0.3s ease;
        }

        .conversation-item-improved:hover {
          //background: linear-gradient(135deg, #e3f2fd 0%, #f0f7ff 100%) !important;
          border-color: #2196f3;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(33, 150, 243, 0.15);
        }

        /* Área de trabajo con theme Bootstrap */
        .conversations-work-area {
          background-color: var(--bs-light);
        }

        .welcome-area {
          background: rgba(var(--bs-light-rgb), 0.8);
          border: 1px solid var(--bs-border-color);
          border-radius: 15px;
          padding: 40px;
          animation: welcomeFadeIn 1s ease-out;
        }

        @keyframes welcomeFadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Dropdown mejorado para acciones */
        .dropdown-menu {
          border: none;
          box-shadow: 0 8px 25px rgba(0,0,0,0.15);
          border-radius: 8px;
          overflow: hidden;
        }

        .dropdown-item {
          transition: all 0.2s ease;
          border-bottom: 1px solid #f0f0f0;
        }

        .dropdown-item:last-child {
          border-bottom: none;
        }

        .dropdown-item:hover {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          transform: translateX(2px);
        }

        /* Estilos para ventanas de conversación - MEJORADOS */
        .conversation-window {
          user-select: none;
          font-family: system-ui, -apple-system, sans-serif;
          backdrop-filter: blur(10px);
          border: 2px solid rgba(255,255,255,0.2);
        }
        
        .conversation-window:hover {
          box-shadow: 0 12px 35px rgba(0,0,0,0.15);
        }
        
        .conversation-window-header {
          user-select: none;
          //background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-bottom: 2px solid rgba(0,0,0,0.05);
        }
        
        .conversation-window-header:hover {
          //background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
          background: --var(--bg-body-hover);
        }
        
        /* NUEVOS: Estilos para el editor de nombres */
        .conversation-window-header .form-control:focus {
          border-color: #007bff;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }

        .conversation-window-header .btn-link:hover {
          color: #007bff !important;
          transform: scale(1.1);
        }
        
        .conversation-window-body {
          //background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
          //background: linear-gradient(135deg, --var(--bg-body) 0%, --var(--bg-body) 100%);
          background: --var(--bg-body);
        }
        
        .conversation-window-body::-webkit-scrollbar {
          width: 6px;
        }
        
        .conversation-window-body::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }
        
        .conversation-window-body::-webkit-scrollbar-thumb {
          background: linear-gradient(45deg, #c1c1c1, #a8a8a8);
          border-radius: 3px;
        }
        
        .conversation-window-body::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(45deg, #a8a8a8, #909090);
        }
        
        /* Estilos para burbujas minimizadas - MEJORADOS */
        .conversation-bubble {
          user-select: none;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          backdrop-filter: blur(10px);
        }
        
        .conversation-bubble:hover {
          transform: scale(1.15);
          box-shadow: 0 8px 25px rgba(0,0,0,0.3);
        }
        
        /* Animación para burbujas de auto-actualización */
        .conversation-bubble[style*="background-color: rgb(0, 123, 255)"] {
          animation: pulse-blue 2s infinite;
        }
        
        @keyframes pulse-blue {
          0% {
            box-shadow: 0 4px 8px rgba(0,0,0,0.2), 0 0 0 0 rgba(0, 123, 255, 0.7);
          }
          70% {
            box-shadow: 0 4px 8px rgba(0,0,0,0.2), 0 0 0 10px rgba(0, 123, 255, 0);
          }
          100% {
            box-shadow: 0 4px 8px rgba(0,0,0,0.2), 0 0 0 0 rgba(0, 123, 255, 0);
          }
        }
        
        /* Estilos para mensajes en ventanas - MEJORADOS */
        .conversation-window .message-card {
          transition: all 0.2s ease;
          border-radius: 12px;
        }

        .conversation-window .message-card:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }
        
        .conversation-window .bot-message {
          background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
          border-top-right-radius: 4px;
        }
        
        .conversation-window .user-message {
          background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);
          border-top-left-radius: 4px;
        }
        
        /* Handle de resize mejorado */
        .resize-handle {
          opacity: 0.3;
          transition: opacity 0.2s ease;
        }

        .resize-handle:hover {
          opacity: 0.8;
          background: linear-gradient(-45deg, transparent 0%, transparent 20%, #007bff 20%, #007bff 30%, transparent 30%, transparent 50%, #007bff 50%, #007bff 60%, transparent 60%);
        }
        
        .conversation-window:hover .resize-handle {
          opacity: 0.6;
        }
        
        /* Estilos para emojis en ventanas */
        .conversation-window .emoji-btn:hover {
          background-color: #f8f9fa !important;
          transform: scale(1.2);
          border-radius: 50%;
        }

        /* NUEVOS: Estilos para información de ventanas activas */
        .active-windows-info {
          animation: slideInRight 0.5s ease-out;
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        /* Responsive sizing para ventanas */
        @media (max-width: 768px) {
          .conversations-layout {
            flex-direction: column;
          }
          
          .conversations-sidebar {
            width: 100% !important;
            height: 20%;
            border-right: none;
            border-bottom: 1px solid #dee2e6;
          }
          
          .conversations-work-area {
            width: 100% !important;
            height: 60%;
          }
          
          .conversation-window {
            min-width: 280px !important;
            min-height: 250px !important;
          }
          
          .conversation-bubble {
            width: 50px !important;
            height: 50px !important;
          }

          .welcome-area {
            padding: 20px;
          }

          .welcome-area h3 {
            fontSize: 1.5rem;
          }

          .welcome-area p {
            fontSize: 14px;
          }
        }
        
        /* Animaciones para ventanas */
        .conversation-window {
          animation: windowFadeIn 0.3s ease-out;
        }
        
        @keyframes windowFadeIn {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        
        .conversation-bubble {
          animation: bubbleBounce 0.4s ease-out;
        }
        
        @keyframes bubbleBounce {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.3);
            opacity: 0.8;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        /* Estilos para los modales */
        .modal.show {
          display: block !important;
          animation: modalFadeIn 0.3s ease-out;
        }

        @keyframes modalFadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        .modal-dialog {
          margin: 1.75rem auto;
          animation: modalSlideIn 0.3s ease-out;
        }

        @keyframes modalSlideIn {
          from {
            transform: translateY(-50px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        .modal-content {
          border-radius: 12px;
          box-shadow: 0 15px 35px rgba(0,0,0,0.3);
          border: none;
        }
        
        /* Mejoras para dispositivos táctiles */
        @media (hover: none) and (pointer: coarse) {
          .conversation-window-header {
            padding: 12px 8px !important;
            touch-action: none;
            min-height: 44px;
          }
          
          .conversation-bubble {
            touch-action: none;
            min-width: 44px !important;
            min-height: 44px !important;
          }
          
          .conversation-window .btn {
            min-height: 44px;
            min-width: 44px;
          }
          
          .resize-handle {
            display: none;
          }

          .conversation-item-improved:hover {
            transform: none;
          }
        }
        
        /* Auto-scroll para mensajes */
        .messages-container {
          scroll-behavior: smooth;
        }
        
        .messages-container::-webkit-scrollbar {
          width: 6px;
        }
        
        .messages-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }
        
        .messages-container::-webkit-scrollbar-thumb {
          background: linear-gradient(45deg, #c1c1c1, #a8a8a8);
          border-radius: 3px;
        }
        
        .messages-container::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(45deg, #a8a8a8, #909090);
        }
        
        /* Scroll suave global */
        * {
          scroll-behavior: smooth;
        }
        
        /* Transiciones mejoradas */
        .btn {
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.15);
        }
        
        /* Estilos mejorados para badges */
        .badge {
          border-radius: 12px;
          font-weight: 500;
          padding: 0.4em 0.6em;
          font-size: 0.7em;
        }
        
        /* Prevenir selección accidental durante drag */
        .conversation-window.dragging,
        .conversation-bubble.dragging {
          user-select: none !important;
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
        }
        
        /* High DPI displays */
        @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
          .conversation-window {
            border-width: 1px;
          }
          
          .conversation-bubble {
            border-width: 1px;
          }
        }
        
        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          .conversation-window,
          .conversation-bubble,
          .btn,
          .welcome-area,
          .active-windows-info,
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }

        /* NUEVOS: Estilos específicos para el editor de nombres */
        .conversation-window-header .form-control {
          background: rgba(255,255,255,0.9);
          border: 2px solid #007bff;
          transition: all 0.2s ease;
        }

        .conversation-window-header .form-control:focus {
          background: white;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
          transform: scale(1.02);
        }

        /* Efectos de guardado exitoso */
        .saving-success {
          animation: saveSuccess 0.6s ease-out;
        }

        @keyframes saveSuccess {
          0% {
            background-color: #d4edda;
            transform: scale(1);
          }
          50% {
            background-color: #c3e6cb;
            transform: scale(1.02);
          }
          100% {
            background-color: transparent;
            transform: scale(1);
          }
        }

        /* Print styles */
        @media print {
          .conversation-window,
          .conversation-bubble,
          .modal {
            display: none !important;
          }
          
          .conversations-sidebar {
            width: 100% !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ConversationsTable;